import { type EventPayload, type RunState, SIGNS, type UnitStatus, signById } from '@orgsim/shared';
import { useEffect, useRef, useState } from 'react';
import { SignPalette } from '../../signs/SignPalette';
import { Icon } from '../Icon';
import { UNIT_STATUS_LABELS } from '../labels';

type Props = {
  state: RunState;
  readOnly: boolean;
  selectedSign: string | null;
  onSelectSign: (s: string | null) => void;
  onPlaceUnit: (unitId: string | null) => void;
  onLocate: (unitId: string) => void;
  focusId: string | null;
  dispatch: (p: EventPayload) => void;
};

export function UnitsPanel({
  state,
  readOnly,
  selectedSign,
  onSelectSign,
  onPlaceUnit,
  onLocate,
  focusId,
  dispatch,
}: Props) {
  const [reqType, setReqType] = useState('rtw');
  const [reqCount, setReqCount] = useState(1);
  const [reqText, setReqText] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (!focusId) return;
    listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [focusId]);

  return (
    <div className="space-y-4">
      <section>
        <div className="panel-section-heading">
          <h3>Einsatzkräfte</h3>
          <span>{Object.keys(state.units).length} gesamt</span>
        </div>
        {Object.keys(state.units).length === 0 ? (
          <p className="panel-description">
            Noch keine Kräfte. Platziere ein Zeichen oder buche eine Nachforderung.
          </p>
        ) : null}
        <ul ref={listRef}>
          {Object.values(state.units).map((u) => (
            <li
              key={u.id}
              data-selected={focusId === u.id}
              className={`unit-row ${focusId === u.id ? 'selected' : ''}`}
            >
              <div className="unit-row-main">
                <Icon name="truck" size={17} />
                <button type="button" onClick={() => onLocate(u.id)} disabled={!state.signs[u.id]}>
                  {u.callsign}
                </button>
                <span className="muted">{signById(u.type).label}</span>
              </div>
              <div className="unit-row-actions">
                <select
                  aria-label={`Status ${u.callsign}`}
                  value={u.status}
                  disabled={readOnly}
                  onChange={(e) =>
                    dispatch({
                      type: 'UnitStatusChanged',
                      unitId: u.id,
                      status: e.target.value as UnitStatus,
                    })
                  }
                  className="rounded bg-neutral-800 px-1 py-0.5 text-xs"
                >
                  {(Object.keys(UNIT_STATUS_LABELS) as UnitStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {UNIT_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                {state.signs[u.id] ? (
                  <button
                    type="button"
                    className="button quiet"
                    aria-label={`${u.callsign} auf Karte zeigen`}
                    onClick={() => onLocate(u.id)}
                  >
                    <Icon name="target" size={15} />
                    Auf Karte
                  </button>
                ) : !readOnly ? (
                  <button type="button" onClick={() => onPlaceUnit(u.id)} className="button">
                    <Icon name="pin" size={14} />
                    Platzieren
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {!readOnly ? (
        <section>
          <h3>Zeichen platzieren</h3>
          <SignPalette selected={selectedSign} onSelect={onSelectSign} />
        </section>
      ) : null}

      <section>
        <h3 className="mb-1 text-xs uppercase tracking-wide text-neutral-400">Nachforderungen</h3>
        {!readOnly ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              dispatch({
                type: 'ReinforcementRequested',
                requestId: crypto.randomUUID(),
                signType: reqType,
                count: reqCount,
                text: reqText || undefined,
              });
              setReqText('');
            }}
            className="mb-2 flex flex-wrap gap-1"
          >
            <input
              type="number"
              min={1}
              value={reqCount}
              onChange={(e) => setReqCount(Number(e.target.value))}
              className="w-14 rounded bg-neutral-800 px-2 py-1"
              aria-label="Anzahl"
            />
            <select
              aria-label="Art der Nachforderung"
              value={reqType}
              onChange={(e) => setReqType(e.target.value)}
              className="rounded bg-neutral-800 px-2 py-1"
            >
              {SIGNS.filter((s) => s.isUnit).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <input
              aria-label="Bemerkung zur Nachforderung"
              value={reqText}
              onChange={(e) => setReqText(e.target.value)}
              placeholder="Bemerkung"
              className="flex-1 rounded bg-neutral-800 px-2 py-1"
            />
            <button type="submit" className="rounded bg-red-600 px-2 py-1 text-black">
              Nachfordern
            </button>
          </form>
        ) : null}
        <ul className="space-y-1">
          {Object.values(state.reinforcements).map((r) => (
            <li key={r.id} className="flex items-center gap-2 rounded bg-neutral-900 px-2 py-1">
              <span className="flex-1">
                {r.count}x {signById(r.signType).label}
                {r.text ? `, ${r.text}` : ''}
              </span>
              {r.arrived ? (
                <span className="text-xs text-green-300">eingetroffen</span>
              ) : !readOnly ? (
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'ReinforcementArrived', requestId: r.id })}
                  className="rounded bg-neutral-800 px-2 py-0.5 text-xs"
                >
                  Eingetroffen
                </button>
              ) : (
                <span className="text-xs text-neutral-500">offen</span>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
